import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Box,
  Typography,
  Tooltip,
  Switch,
  FormControlLabel,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrash';
import ScienceIcon from '@mui/icons-material/Science';

import clientService from 'src/clientService';
import { SoilElementResponse } from 'src/api/Api';
import { SoilElementCreateDialog } from './SoilElementCreateDialog';
import { useSnackbar, SnackbarType } from 'src/view/components/SnackbarService';

interface SoilElementsCatalogModalProps {
  open: boolean;
  onClose: () => void;
  onElementsUpdated?: () => void;
}

export const SoilElementsCatalogModal: React.FC<SoilElementsCatalogModalProps> = ({
  open,
  onClose,
  onElementsUpdated,
}) => {
  const { showSnackbar } = useSnackbar();
  const [elements, setElements] = useState<SoilElementResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  const fetchElements = async () => {
    setLoading(true);
    try {
      const response = await clientService.api.listSoilElementsApiV1SoilChemistryElementsGet({
        is_active: showInactive ? undefined : true,
      });
      setElements(response.data || []);
    } catch (err) {
      console.error('Failed to fetch soil elements catalog:', err);
      showSnackbar({
        message: 'Failed to load soil elements catalog',
        type: SnackbarType.ERROR,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchElements();
    }
  }, [open, showInactive]);

  const handleToggleActive = async (element: SoilElementResponse) => {
    try {
      if (element.is_active !== false) {
        // Soft-delete
        await clientService.api.deleteSoilElementApiV1SoilChemistryElementsElementIdDelete(
          element.element_id
        );
        showSnackbar({
          message: `Deactivated element "${element.name}"`,
          type: SnackbarType.SUCCESS,
        });
      } else {
        // Reactivate
        await clientService.api.updateSoilElementApiV1SoilChemistryElementsElementIdPut(
          element.element_id,
          { is_active: true }
        );
        showSnackbar({
          message: `Reactivated element "${element.name}"`,
          type: SnackbarType.SUCCESS,
        });
      }
      fetchElements();
      if (onElementsUpdated) onElementsUpdated();
    } catch (err) {
      console.error('Failed to update element active status:', err);
      showSnackbar({
        message: 'Failed to update element status',
        type: SnackbarType.ERROR,
      });
    }
  };

  const handleElementCreated = (_newElement: SoilElementResponse) => {
    fetchElements();
    if (onElementsUpdated) onElementsUpdated();
  };

  const filteredElements = elements.filter(
    (el) =>
      (el.name && el.name.toLowerCase().includes(search.toLowerCase())) ||
      (el.symbol && el.symbol.toLowerCase().includes(search.toLowerCase())) ||
      (el.category && el.category.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ScienceIcon color="primary" />
            <Box>
              <Typography variant="h6" fontWeight="bold">
                Soil Elements Catalog Management
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Configure chemical parameters available for lab testing results.
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setIsCreateDialogOpen(true)}
            size="small"
          >
            Add New Element
          </Button>
        </DialogTitle>

        <DialogContent dividers>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <TextField
              size="small"
              placeholder="Search elements by name or symbol..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 320 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={showInactive}
                  onChange={(e) => setShowInactive(e.target.checked)}
                  color="primary"
                />
              }
              label={<Typography variant="body2">Show Deactivated Elements</Typography>}
            />
          </Box>

          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Symbol</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Element Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Category</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Default Unit</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredElements.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        {loading ? 'Loading catalog elements...' : 'No soil elements found.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredElements.map((el) => {
                    const isActive = el.is_active !== false;
                    return (
                      <TableRow key={el.element_id} hover sx={{ opacity: isActive ? 1 : 0.6 }}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            {el.symbol}
                          </Typography>
                        </TableCell>
                        <TableCell>{el.name}</TableCell>
                        <TableCell>
                          <Chip
                            label={el.category || 'General'}
                            size="small"
                            variant="outlined"
                            color="default"
                          />
                        </TableCell>
                        <TableCell>{el.default_unit || '-'}</TableCell>
                        <TableCell>
                          <Chip
                            label={isActive ? 'Active' : 'Inactive'}
                            size="small"
                            color={isActive ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title={isActive ? 'Deactivate (Soft-Delete)' : 'Reactivate'}>
                            <IconButton
                              size="small"
                              color={isActive ? 'error' : 'primary'}
                              onClick={() => handleToggleActive(el)}
                            >
                              {isActive ? <DeleteOutlineIcon fontSize="small" /> : <RestoreFromTrashIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <SoilElementCreateDialog
        open={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        onSuccess={handleElementCreated}
      />
    </>
  );
};
